import { Panel, PanelGroup, PanelResizeHandle } from "react-resizable-panels";
import { Button, Modal, Tabs } from "antd";
import { PanelLeftClose, PanelRightClose } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import "./document-creator.css";
import ViewerLayout from "./ViewerLayout";
import {
  Converse,
  MicRecorderProvider,
  ModusProvider,
  RoadMapProvider,
  useModus,
  VojomoLoader
} from "@vojomo-core/shared";
import { TopicProvider, useTopic } from "./hooks/use-topics";
import { getSafeStreamingText } from "utils/helpers/streamSafe";
import { useParams } from "react-router-dom";
import { TopicTab } from "./components/tabs/TopicTab";
import { TopicContent } from "./components/content/TopicContent";
import { SourcesContent } from "./components/content/SourcesContent";
import { RoadmapContent } from "./components/content/RoadmapContent";
import { CollaborativeProvider } from "./hooks/use-collaborative";
import { CommentsPanel } from "./components/comments/CommentsPanel";

export const TopicViewer = ({ topicIdProps, inModalView, readOnly }) => {
  const { topicId } = useParams();

  const id = useMemo(() => {
    return topicIdProps || topicId;
  }, [topicIdProps, topicId]);

  return (
    <MicRecorderProvider>
      <ModusProvider refId={id}>
        <RoadMapProvider>
          <TopicProvider readOnly={readOnly} topicId={id}>
            <ViewerLayout inModalView={inModalView}>
              <TopicViewerInternal inModalView={inModalView} />
            </ViewerLayout>
          </TopicProvider>
        </RoadMapProvider>
      </ModusProvider>
    </MicRecorderProvider>
  );
};

const TopicViewerInternal = ({ inModalView }) => {
  const [chatOpen, setChatOpen] = useState(true);
  const [docOpen, setDocOpen] = useState(true);
  const [commentsOpen, setCommentsOpen] = useState(
    localStorage.getItem("isCommentsPanelOpen") === "true"
  );

  const {
    topic,
    output,
    setOutputsList,

    isStreaming,
    setIsStreaming,

    topicTitle,
    setTopicTitle,

    topicType,
    setTopicType,

    setCurrentOutputText,
    setPeakAmbitionDate,

    activeTab,
    setActiveTab,

    setTopicTypeOpen,

    isTopicTypeModalVisible,
    setIsTopicTypeModalVisible,

    createNewTopicType,

    changedDocType,
    setChangedDocType,

    refreshImage,

    hasWriteAccess,

    isLoadingTopic,
    isLoadingOutput
  } = useTopic();
  const { sendMessage, modusChatContext } = useModus();

  useEffect(() => {
    localStorage.setItem("isCommentsPanelOpen", commentsOpen.toString());
  }, [commentsOpen]);

  const tabKeyForActiveTab = useMemo(() => {
    switch (activeTab) {
      case "Sources":
        return "sources";
      case "Roadmap":
        return "roadmap";
      case "BriefingDoc":
        return "briefingDoc";
      case "Topic":
      default:
        return "content";
    }
  }, [activeTab]);

  const activeTabForKey = (key: string) => {
    switch (key) {
      case "sources":
        return "Sources";
      case "roadmap":
        return "Roadmap";
      case "briefingDoc":
        return "BriefingDoc";
      case "content":
      default:
        return "Topic";
    }
  };

  const tabItems = useMemo(() => {
    const arr = [
      {
        key: "content",
        label: <TopicTab />,
        children: <TopicContent />
      }
    ];

    if (hasWriteAccess) {
      if (topicType === "building-block") {
        arr.push({
          key: "roadmap",
          label: <span>Roadmap</span>,
          children: <RoadmapContent />
        });
      }

      if (topicType !== "briefing-doc") {
        arr.push({
          key: "sources",
          label: <span>Sources</span>,
          children: <SourcesContent />
        });
      }
    }

    return arr;
  }, [topicType, topic, hasWriteAccess]);

  const headerActionButtons = useMemo(() => {
    const buttons = [];

    if (!output) {
      if (!topicTitle) {
        // buttons.push({
        //   text: "Set title",
        //   onClick: () => {
        //     setActiveTab("Topic");
        //     setIsTitleInputFocused(true);
        //     setIsTitleTyping(true);
        //   }
        // });
      }

      if (!topicType) {
        buttons.push({
          text: "Set type",
          onClick: () => {
            setTopicTypeOpen(true);
          }
        });
      }
    } else if (output) {
      if (modusChatContext?.messages?.length > 1) {
        buttons.push({
          text: "New question",
          onClick: () => {
            sendMessage("Ask me a different question.");
          }
        });
      } else {
        buttons.push({
          text: "Get a question",
          onClick: () => {
            sendMessage("Ask me a question to help me.");
          }
        });
      }
    }

    return buttons;
  }, [modusChatContext, output, topicType, topicTitle]);

  const topicHeaderTitle = useMemo(() => {
    if (topicType === "building-block" || topicType === "briefing-doc") {
      return topicTitle;
    } else {
      return topicType;
    }
  }, [topicType, topicTitle]);

  const streamRawRef = useRef<string>("");

  if (isLoadingTopic || topic === undefined) {
    return <VojomoLoader />;
  }

  if (topic === null) {
    return (
      <div className="flex justify-center items-center h-[90vh]">
        <p className="text-center font-bold">
          There is no team document available, only owners can create one.
        </p>
      </div>
    );
  } else if (topic["_id"] === null) {
    return (
      <div className="flex justify-center items-center h-[90vh]">
        <p className="text-center font-bold">
          Document not found (the document may have been deleted)
        </p>
      </div>
    );
  }

  return (
    <CollaborativeProvider
      roomName={output?.["_id"]}
      isDisabled={!output || isLoadingOutput || isStreaming}
    >
      <div className="page-root">
        <div className="page-body">
          {!chatOpen && (
            <div className="side-rail" onClick={() => setChatOpen(true)}>
              <div className="rail-content">
                <PanelRightClose className="rail-icon" />
                <span className="rail-label rail-label-vertical">Open Vo</span>
              </div>
            </div>
          )}

          <div className="page-main">
            <PanelGroup
              key={`panel-group-${chatOpen ? "chat" : "nochat"}-${
                docOpen ? "doc" : "nodoc"
              }`}
              direction="horizontal"
              className="resizable-group"
            >
              {chatOpen && !inModalView && (
                <>
                  <Panel
                    defaultSize={30}
                    minSize={25}
                    maxSize={50}
                    className={`panel chat-panel ${!docOpen ? "panel-center" : ""}`}
                  >
                    <div className="chat-panel-shell">
                      <div className="panel-header">
                        <div className="panel-title">{topicHeaderTitle}</div>
                        <Button
                          type="text"
                          size="small"
                          className="icon-button-muted"
                          onClick={() => setChatOpen(false)}
                          style={{ zIndex: 6 }}
                        >
                          <PanelLeftClose className="icon-16" />
                        </Button>
                      </div>
                      <div className="chat-panel-content">
                        <Converse
                          size={250}
                          fontSizeOverride="14px"
                          orbFontSizeOverride="10px"
                          showTranscriptIcon={true}
                          placeHolder="Type to talk to Vo"
                          headerActionButtons={headerActionButtons}
                          onProcess={async (transcript: string) => {
                            await sendMessage(transcript, (msg) => {
                              if (
                                msg.data.message_type ===
                                "new_output_streaming_starting"
                              ) {
                                setOutputsList(msg.data.outputs_list);
                                setIsStreaming(true);
                                streamRawRef.current = "";
                              }

                              if (
                                msg.data.message_type ===
                                "output_streamed_chunk"
                              ) {
                                const nextRaw = msg.data.text ?? "";
                                streamRawRef.current = nextRaw.startsWith(
                                  streamRawRef.current
                                )
                                  ? nextRaw
                                  : streamRawRef.current + nextRaw;
                                setCurrentOutputText(
                                  getSafeStreamingText(streamRawRef.current)
                                );
                              }

                              if (
                                msg.data.message_type ===
                                "new_output_streaming_ended"
                              ) {
                                setIsStreaming(false);
                                setCurrentOutputText(streamRawRef.current);
                                streamRawRef.current = "";
                              }

                              if (
                                msg.data.message_type === "update_topic_type"
                              ) {
                                const newType = msg.data.new_type;

                                for (let i = 1; i <= newType.length; i++) {
                                  setTimeout(() => {
                                    setTopicType(newType.substring(0, i));
                                  }, i * 20);
                                }
                              }

                              if (
                                msg.data.message_type === "update_topic_title"
                              ) {
                                const newTitle = msg.data.new_title;

                                for (let i = 1; i <= newTitle.length; i++) {
                                  setTimeout(() => {
                                    setTopicTitle(newTitle.substring(0, i));
                                  }, i * 20);
                                }
                              }

                              if (
                                msg.data.message_type ===
                                "updated_peak_ambition_date"
                              ) {
                                setPeakAmbitionDate(
                                  msg.data.peak_ambition_date
                                );
                              }

                              if (
                                msg.data.message_type ===
                                "generating_new_cover_image"
                              ) {
                                refreshImage();
                              }

                              if (
                                msg.data.message_type ===
                                "prompt_change_topic_type"
                              ) {
                                {
                                  setChangedDocType(msg.data.new_type);
                                  setIsTopicTypeModalVisible(true);
                                }
                              }
                            });
                          }}
                        />
                        {!hasWriteAccess && (
                          <div className="chat-locked-overlay">
                            <p
                              style={{
                                fontSize: "16px",
                                fontWeight: "600",
                                color: "#666",
                                textAlign: "center",
                                padding: "20px"
                              }}
                            >
                              You don't have access to edit this{" "}
                              {topicType == "building-block"
                                ? "building block "
                                : "document "}{" "}
                              with Vo.
                              <br />
                              <br />
                              Coming soon, you'll be able to talk with Vo about{" "}
                              {topicType == "building-block"
                                ? "building blocks "
                                : "documents "}
                              you don't have edit access to.
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  </Panel>

                  {docOpen && <PanelResizeHandle className="resize-handle" />}
                </>
              )}

              {docOpen && (
                <Panel
                  defaultSize={chatOpen ? 50 : 100}
                  className={`panel doc-panel ${!chatOpen ? "panel-full" : ""}`}
                >
                  <Tabs
                    items={tabItems}
                    activeKey={tabKeyForActiveTab}
                    onChange={(key) => {
                      const nextTab = activeTabForKey(key);
                      if (nextTab !== activeTab) {
                        setActiveTab(nextTab);
                      }
                    }}
                    className="doc-tabs"
                    tabBarExtraContent={
                      inModalView ? (
                        <></>
                      ) : (
                        <div className="doc-header-actions">
                          {!chatOpen && (
                            <Button
                              size="small"
                              className="doc-open-vo"
                              onClick={() => setChatOpen(true)}
                            >
                              <PanelRightClose className="icon-14" />
                              Open Vo
                            </Button>
                          )}
                          <Button
                            type="text"
                            size="small"
                            className="icon-button-muted"
                            onClick={() => setDocOpen(false)}
                          >
                            <PanelRightClose className="icon-16" />
                          </Button>
                        </div>
                      )
                    }
                  />
                </Panel>
              )}

              {!docOpen && (
                <div className="side-rail" onClick={() => setDocOpen(true)}>
                  <div className="rail-content">
                    <PanelLeftClose className="rail-icon" />
                    <span className="rail-label rail-label-vertical">
                      Open Document
                    </span>
                  </div>
                </div>
              )}

              <CommentsPanel
                commentsOpen={commentsOpen}
                setCommentsOpen={setCommentsOpen}
              />
            </PanelGroup>
          </div>
        </div>
        {isTopicTypeModalVisible && (
          <Modal
            className="w-[400px]"
            open={true}
            closable={true}
            onCancel={() => setIsTopicTypeModalVisible(false)}
            footer={[
              <Button
                type="default"
                key="back"
                onClick={() => setIsTopicTypeModalVisible(false)}
              >
                Cancel
              </Button>,
              <Button
                key="submit"
                type="default"
                onClick={() => {
                  setIsTopicTypeModalVisible(false);
                  createNewTopicType(changedDocType);
                }}
              >
                Create
              </Button>
            ]}
          >
            <p>
              Create new <b>{changedDocType}</b> from this document's content?
            </p>
          </Modal>
        )}
      </div>
    </CollaborativeProvider>
  );
};
